import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useAllComponents, useAllQueues } from '@/api/queries';
import { useNow } from '@/app/preferences';
import { proposalPath, runsPath, scopePath } from '@/app/scope';
import { HealthBadge } from '@/components/badges';
import { ArrowRight, CheckCircle, Clock, EyeOff, Warning } from '@/components/icons';
import { EmptyState, Loading, Panel, Skeleton } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { HEALTH_LABEL, SCOPE_LABEL, SCOPE_SHORT, worstOf } from '@/domain/health';
import { formatDuration } from '@/domain/time';
import { SCOPES, type ComponentView, type Proposal, type Scope } from '@/domain/types';

import { kindLabel, recoveryOverdue } from '../queue/queue';

/**
 * The overview is two things and no boxes: a health title per scope, and one prioritised list
 * of what needs a person. Everything else lives in the scope tabs.
 */
export function OverviewPage() {
  const components = useAllComponents();
  const queues = useAllQueues();
  const now = useNow();

  const views = new Map<Scope, ComponentView[]>();
  components.forEach((result, i) => {
    if (result.data) views.set(SCOPES[i]!, result.data.components);
  });
  const configs = new Map<string, ComponentView['config']>();
  for (const list of views.values()) {
    for (const c of list) configs.set(`${c.config.scope}#${c.config.capability_id}`, c.config);
  }

  const todos = buildTodos(
    views,
    queues.map((q) => q.data ?? []),
    configs,
    now,
  );
  const loading = components.some((c) => c.isPending) || queues.some((q) => q.isPending);
  const asOf = components.find((c) => c.data)?.data?.as_of;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-page font-medium">Overview</h1>
        {asOf ? (
          <p className="text-body text-ink-3">
            As of <Timestamp iso={asOf} mode="clock" />
          </p>
        ) : null}
      </div>

      {/* One card per scope: its health title and counts. */}
      <section aria-labelledby="health-heading">
        <h2 id="health-heading" className="sr-only">
          Health by scope
        </h2>
        <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 xl:grid-cols-4">
          {SCOPES.map((scope, i) => (
            <HealthTitle
              key={scope}
              scope={scope}
              list={views.get(scope)}
              pending={queues[i]?.data?.length ?? 0}
              loading={components[i]?.isPending ?? true}
            />
          ))}
        </ul>
      </section>

      {/* The to-do list on its own card, in the same frame as every other card. */}
      <Panel
        title="Needs you"
        titleId="todo-heading"
        count={todos.length}
        aria-labelledby="todo-heading"
        bodyClassName="p-0"
      >
        {loading && todos.length === 0 ? (
          <Loading />
        ) : todos.length === 0 ? (
          <EmptyState
            compact
            icon={<CheckCircle size={24} className="text-status-ok-ink" />}
            title="Nothing needs you right now."
          />
        ) : (
          <ol className="m-0 list-none divide-y divide-line p-0">
            {todos.map((todo) => (
              <TodoRow key={todo.id} todo={todo} />
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}

function HealthTitle({
  scope,
  list,
  pending,
  loading,
}: {
  scope: Scope;
  list: ComponentView[] | undefined;
  pending: number;
  loading: boolean;
}) {
  const worst = list ? worstOf(list.map((c) => c.state.published_status)) : null;
  const blind = list?.filter((c) => c.state.blind_since).length ?? 0;
  const disagree =
    list?.filter(
      (c) =>
        c.state.implied_status !== c.state.published_status &&
        c.state.published_status !== 'maintenance',
    ).length ?? 0;
  const notes: string[] = [];
  if (pending > 0) notes.push(`${pending} to decide`);
  if (disagree > 0) notes.push(`${disagree} disagree${disagree === 1 ? 's' : ''} with the page`);
  if (blind > 0) notes.push(`${blind} blind`);

  return (
    <Panel as="li" className="min-w-0" bodyClassName="px-5 py-4">
      <Link
        to={scopePath(scope)}
        className="text-meta font-medium text-ink-3 no-underline transition-tone hover:text-ink hover:underline"
      >
        {SCOPE_LABEL[scope]}
      </Link>
      <div className="mt-1 text-heading">
        {worst ? (
          <HealthBadge health={worst} variant="text" size="lg" />
        ) : loading ? (
          <Skeleton className="h-5 w-36" />
        ) : (
          <span className="text-ink-3">Unknown</span>
        )}
      </div>
      <p className="mt-1 text-body text-ink-2">
        {list ? (
          notes.length > 0 ? (
            notes.join(' · ')
          ) : (
            `All ${list.length} components operational`
          )
        ) : loading ? (
          <Skeleton className="h-3 w-44" />
        ) : (
          ' '
        )}
      </p>
    </Panel>
  );
}

/* ---- the to-do list ---- */

type Priority = 0 | 1 | 2 | 3;

interface Todo {
  id: string;
  priority: Priority;
  /** Sort key within a priority: oldest first. */
  since: string;
  scope: Scope;
  name: string;
  action: string;
  detail: ReactNode;
  meta: ReactNode;
  href: string;
}

function buildTodos(
  views: Map<Scope, ComponentView[]>,
  queues: Proposal[][],
  configs: Map<string, ComponentView['config']>,
  now: number,
): Todo[] {
  const todos: Todo[] = [];

  queues.forEach((queue, i) => {
    const scope = SCOPES[i]!;
    for (const p of queue) {
      const config = configs.get(p.component_key);
      const name = config?.display_name ?? p.capability_id;
      const change = (
        <span className="inline-flex flex-wrap items-center gap-x-1.5">
          <HealthBadge health={p.from_status} variant="text" size="sm" />
          <ArrowRight size={12} className="text-ink-3" aria-hidden="true" />
          <HealthBadge health={p.to_status} variant="text" size="sm" />
        </span>
      );
      if (p.status === 'publish_failed') {
        todos.push({
          id: p.proposal_id,
          priority: 0,
          since: p.approved_at ?? p.created_at,
          scope,
          name,
          action: 'Retry the publish',
          detail: (
            <>
              {change}
              {p.last_error ? <span className="text-ink-3"> · {p.last_error}</span> : null}
            </>
          ),
          meta: p.approved_at
            ? `approved ${formatDuration(now - Date.parse(p.approved_at))} ago`
            : null,
          href: proposalPath(p.proposal_id),
        });
        continue;
      }
      const overdue = recoveryOverdue(p, config, now);
      const recovery = p.to_status === 'ok';
      todos.push({
        id: p.proposal_id,
        priority: overdue ? 1 : 2,
        since: p.created_at,
        scope,
        name,
        action: recovery ? 'Confirm the recovery' : 'Approve or reject',
        detail: (
          <>
            {change}
            <span className="text-ink-3"> · {kindLabel(p)}</span>
          </>
        ),
        meta: (
          <>
            waiting {formatDuration(now - Date.parse(p.created_at))}
            {overdue ? <span className="text-status-impaired-ink"> · overdue</span> : null}
          </>
        ),
        href: proposalPath(p.proposal_id),
      });
    }
  });

  for (const [scope, list] of views) {
    for (const c of list) {
      if (c.state.blind_since) {
        todos.push({
          id: `blind-${scope}-${c.config.capability_id}`,
          priority: 3,
          since: c.state.blind_since,
          scope,
          name: c.config.display_name,
          action: 'Check the runner',
          detail: (
            <>
              Blind since <Timestamp iso={c.state.blind_since} mode="auto" />
              {c.state.last_observed_at ? (
                <>
                  {' '}
                  · last run <Timestamp iso={c.state.last_observed_at} mode="auto" />
                </>
              ) : (
                ' · never reported'
              )}
            </>
          ),
          meta: `expected every ${c.config.runner_cadence_minutes} min`,
          href: runsPath(scope, c.config.capability_id),
        });
      }
      if (c.state.publish_uncertain) {
        todos.push({
          id: `uncertain-${scope}-${c.config.capability_id}`,
          priority: 3,
          since: c.state.evaluated_at ?? '',
          scope,
          name: c.config.display_name,
          action: 'Confirm the page',
          detail: `The last write got no response; the page may not show ${HEALTH_LABEL[c.state.published_status]}.`,
          meta: null,
          href: scopePath(scope),
        });
      }
    }
  }

  return todos.sort((a, b) => a.priority - b.priority || a.since.localeCompare(b.since));
}

const GLYPH: Record<Priority, { icon: ReactNode; label: string }> = {
  0: { icon: <Warning size={16} className="text-status-failed-ink" />, label: 'Blocked' },
  1: { icon: <Warning size={16} className="text-status-impaired-ink" />, label: 'Overdue' },
  2: { icon: <Clock size={16} className="text-brand" />, label: 'Decision' },
  3: { icon: <EyeOff size={16} className="text-ink-3" />, label: 'Worth a look' },
};

/** One row: glyph, name and scope with the change beneath, the action, and how long it has waited. */
function TodoRow({ todo }: { todo: Todo }) {
  const glyph = GLYPH[todo.priority];
  return (
    <li>
      {/* The row fills the card, so its focus ring is drawn inside it rather than clipped by the card. */}
      <Link
        to={todo.href}
        className="group grid grid-cols-[20px_minmax(0,1fr)] items-start gap-x-4 gap-y-1 px-5 py-3 text-ink no-underline transition-tone hover:bg-surface-2 hover:no-underline focus-visible:-outline-offset-2 sm:grid-cols-[20px_minmax(0,1.5fr)_minmax(0,1fr)_160px]"
      >
        <span className="mt-0.5" title={glyph.label}>
          <span className="sr-only">{glyph.label}: </span>
          {glyph.icon}
        </span>
        <span className="min-w-0">
          <span className="text-lead font-medium">{todo.name}</span>
          <span className="text-body text-ink-3"> · {SCOPE_SHORT[todo.scope]}</span>
          <span className="mt-0.5 block text-body text-ink-2">{todo.detail}</span>
        </span>
        <span className="col-start-2 inline-flex items-center gap-1 text-body font-medium text-link group-hover:underline sm:col-start-3">
          {todo.action}
          <ArrowRight size={14} aria-hidden="true" />
        </span>
        <span className="col-start-2 text-meta text-ink-3 tabular sm:col-start-4 sm:text-right">
          {todo.meta}
        </span>
      </Link>
    </li>
  );
}
